/**
 * Detachable Panel Hook
 * Manages the lifecycle of a detached panel window:
 * - Opening/closing the child window via window.open
 * - Broadcasting state changes to the child
 * - Receiving correction updates and action events from the child
 * - Detecting child window close (polling)
 * - Cleanup on parent unmount/navigation
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useBroadcastSync, MSG } from './useBroadcastSync';

/**
 * @param {object} options
 * @param {string} options.reviewItemId - Current item ID (for the detached URL)
 * @param {object} options.currentItem - Full current queue item
 * @param {object} options.correction - Current correction state
 * @param {number} options.documentZoom - Document viewer zoom level
 * @param {string} options.documentViewMode - Document viewer mode (html/text/raw)
 * @param {boolean} options.canApprove - Whether the user can approve
 * @param {boolean} options.isAssignedToMe - Whether assigned to current user
 * @param {object} options.handlers - Correction and action handlers from parent
 */
export const useDetachablePanel = ({
  reviewItemId,
  currentItem,
  correction,
  documentZoom,
  documentViewMode,
  canApprove,
  isAssignedToMe,
  handlers
}) => {
  const [detachedPanel, setDetachedPanel] = useState(null); // null | 'viewer' | 'editor'
  const [popupBlocked, setPopupBlocked] = useState(false);
  const childWindowRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const syncTimeoutRef = useRef(null);

  // Counter to force state sync when child signals ready
  const [syncTrigger, setSyncTrigger] = useState(0);

  // Handle incoming messages from the child window
  const handleMessage = useCallback((type, payload) => {
    switch (type) {
      case MSG.CHILD_READY:
        // Child loaded and is requesting initial state — trigger immediate sync
        setSyncTrigger(c => c + 1);
        break;

      case MSG.CORRECTION_UPDATE: {
        const { action, field, value, legIndex, legData, parsedData, documentType, smartInputValue, notes } = payload;
        switch (action) {
          case 'updateField': handlers.updateField?.(field, value); break;
          case 'updateLeg': handlers.updateLeg?.(legIndex, legData); break;
          case 'addLeg': handlers.addLeg?.(); break;
          case 'removeLeg': handlers.removeLeg?.(legIndex); break;
          case 'changeDocumentType': handlers.changeDocumentType?.(documentType); break;
          case 'applyParsedData': handlers.applyParsedData?.(parsedData); break;
          case 'setSmartInputValue': handlers.setSmartInputValue?.(smartInputValue); break;
          case 'setNotes': handlers.setNotes?.(notes); break;
          case 'resetCorrection': handlers.resetCorrection?.(); break;
          default: break;
        }
        break;
      }

      case MSG.ACTION_EVENT: {
        const { action } = payload;
        switch (action) {
          case 'approve': handlers.onApprove?.(); break;
          case 'approveAndNext': handlers.onApproveAndNext?.(); break;
          case 'reject': handlers.onReject?.(); break;
          case 'skip': handlers.onSkip?.(); break;
          case 'assign': handlers.onAssign?.(); break;
          case 'delete': handlers.onDelete?.(); break;
          case 'reset': handlers.resetCorrection?.(); break;
          default: break;
        }
        break;
      }

      case MSG.REATTACH_REQUEST:
        // Child requested reattach
        reattachPanel();
        break;

      case MSG.CHILD_CLOSING:
        // Child is closing
        setDetachedPanel(null);
        childWindowRef.current = null;
        break;

      default:
        break;
    }
  }, [handlers]);

  const { sendMessage, isConnected } = useBroadcastSync(handleMessage);

  /**
   * Send full state snapshot to child
   */
  const sendStateSync = useCallback(() => {
    if (!detachedPanel) return;

    sendMessage(MSG.STATE_SYNC, {
      currentItem,
      correction,
      documentZoom,
      documentViewMode,
      canApprove,
      isAssignedToMe
    });
  }, [detachedPanel, sendMessage, currentItem, correction, documentZoom, documentViewMode, canApprove, isAssignedToMe]);

  /**
   * Open a panel in a new window
   */
  const detachPanel = useCallback((panelName) => {
    if (detachedPanel) {
      // Only one panel can be detached at a time
      console.warn('A panel is already detached. Reattach it first.');
      return false;
    }

    if (!reviewItemId) return false;

    // Calculate window dimensions — roughly half screen width, full height
    const width = Math.round(window.screen.availWidth * 0.5);
    const height = window.screen.availHeight;
    const left = panelName === 'viewer' ? 0 : window.screen.availWidth - width;
    const top = 0;

    const url = `/parsing-review/${reviewItemId}/detached/${panelName}`;
    const features = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`;

    const childWindow = window.open(url, `parsing-review-${panelName}`, features);

    if (!childWindow) {
      setPopupBlocked(true);
      return false;
    }

    setPopupBlocked(false);
    childWindowRef.current = childWindow;
    setDetachedPanel(panelName);

    return true;
  }, [detachedPanel, reviewItemId]);

  /**
   * Close the child window and restore split layout
   */
  const reattachPanel = useCallback(() => {
    sendMessage(MSG.REATTACH);

    if (childWindowRef.current && !childWindowRef.current.closed) {
      childWindowRef.current.close();
    }

    childWindowRef.current = null;
    setDetachedPanel(null);
  }, [sendMessage]);

  // Poll to detect if child window was closed manually
  useEffect(() => {
    if (!detachedPanel || !childWindowRef.current) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    pollIntervalRef.current = setInterval(() => {
      if (childWindowRef.current?.closed) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        childWindowRef.current = null;
        setDetachedPanel(null);
      }
    }, 500);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [detachedPanel]);

  // Broadcast state to child whenever relevant state changes (debounced)
  // syncTrigger changes when CHILD_READY is received, forcing an immediate sync
  useEffect(() => {
    if (!detachedPanel) return;

    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = setTimeout(() => {
      sendStateSync();
    }, 50);

    return () => {
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    };
  }, [detachedPanel, sendStateSync, syncTrigger]);

  // Close child window on parent unmount or navigation away
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (childWindowRef.current && !childWindowRef.current.closed) {
        sendMessage(MSG.REATTACH);
        childWindowRef.current.close();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      handleBeforeUnload();
    };
  }, [sendMessage]);

  return {
    detachedPanel,
    isDetached: !!detachedPanel,
    popupBlocked,
    isConnected,
    detachPanel,
    reattachPanel,
    setPopupBlocked
  };
};

export default useDetachablePanel;
